"use client";

import { ArchiveIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import type { ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/**
 * The record sheet's parts as markup. RecordEditorSheet owns drafts, saving
 * and the discussion thread and renders its frame through these, so a
 * fixture record composes the same header, tabs, fields and footer.
 */

export type RecordEditorTab = "details" | "discussion";

/** "3 of 12" with previous and next, in the sheet header. */
export function RecordSheetNavigation({
  navigation,
  onNavigate,
}: {
  navigation: { index: number; total: number };
  onNavigate?: (direction: -1 | 1) => void;
}) {
  return (
    <div className="flex items-center gap-1" aria-label="Record navigation">
      <span
        className="mr-1 text-xs tabular-nums text-muted-foreground"
        aria-live="polite"
      >
        {navigation.index + 1} of {navigation.total}
      </span>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        aria-label="Previous record"
        title="Previous record (Arrow up)"
        disabled={navigation.index === 0}
        onClick={() => onNavigate?.(-1)}
      >
        <ChevronUpIcon />
      </Button>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        aria-label="Next record"
        title="Next record (Arrow down)"
        disabled={navigation.index === navigation.total - 1}
        onClick={() => onNavigate?.(1)}
      >
        <ChevronDownIcon />
      </Button>
    </div>
  );
}

/** Details and Discussion, with the record's actions on the right. */
export function RecordSheetTabRow({
  tab,
  onTabChange,
  discussionCount,
  actions,
}: {
  tab: RecordEditorTab;
  onTabChange?: (tab: RecordEditorTab) => void;
  discussionCount: number;
  actions?: ReactNode;
}) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 pb-2"
      data-slot="record-tab-row"
    >
      <Tabs
        value={tab}
        onValueChange={(value) => onTabChange?.(value as RecordEditorTab)}
      >
        <TabsList variant="navigation" aria-label="Record sections">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="discussion">
            Discussion{discussionCount > 0 ? ` (${discussionCount})` : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex items-center gap-1">{actions}</div>
    </div>
  );
}

/** The scrolling Details body: photographs, then the field grid. */
export function RecordSheetDetailsBody({ children }: { children: ReactNode }) {
  return <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>;
}

export function RecordFieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

/** One field: its label, its control or value, and any error. Compact
 *  fields sit two to a row. */
export function RecordFieldFrame({
  label,
  required,
  compact,
  error,
  containerRef,
  below,
  children,
}: {
  label: string;
  required?: boolean;
  compact: boolean;
  error?: string;
  containerRef?: Ref<HTMLDivElement>;
  /** Settings shown beneath the field, outside its label. */
  below?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      ref={containerRef}
      className={cn("block space-y-1", compact ? null : "sm:col-span-2")}
    >
      <label className="block space-y-1">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
          {required ? " *" : ""}
        </span>
        {children}
        {error ? (
          <span role="alert" className="text-xs text-destructive">
            {error}
          </span>
        ) : null}
      </label>
      {below}
    </div>
  );
}

/** A value a source or workflow owns: shown, not edited. */
export function RecordSystemValue({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-9 rounded-md border border-border/70 bg-muted/35 px-3 py-2 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/** Archive on the left; Cancel and Save on the right. */
export function RecordSheetFooter({
  showArchive,
  saving,
  saveDisabled,
  onArchive,
  onCancel,
  onSave,
}: {
  showArchive: boolean;
  saving: boolean;
  saveDisabled: boolean;
  onArchive?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
}) {
  return (
    <div className="flex items-center gap-2 border-t border-border/60 p-4">
      {showArchive ? (
        <Button
          size="sm"
          variant="outline"
          disabled={saving}
          onClick={onArchive}
        >
          <ArchiveIcon data-icon="inline-start" />
          Archive
        </Button>
      ) : null}
      <span className="flex-1" />
      <Button size="sm" variant="outline" disabled={saving} onClick={onCancel}>
        Cancel
      </Button>
      <Button size="sm" disabled={saveDisabled} onClick={onSave}>
        {saving ? "Saving..." : "Save"}
      </Button>
    </div>
  );
}
